import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';

type Unit = {
  id: string;
  vehicleId: string;
  licensePlate?: string | null;
  make?: string | null;
  model?: string | null;
  year?: number | null;
  currentMileage?: number | null;
  nextServiceDate?: string | null;
  nextServiceMileage?: number | null;
  insuranceDueDate?: string | null;
  vtvDueDate?: string | null;
  operationalStatus?: string | null;
  priority?: string | null;
};

type TimelineItem = {
  source: string;
  sourceId: string;
  date?: string | null;
  status?: string | null;
  description?: string | null;
  amount?: number | null;
};

type Alert = {
  id: string;
  title: string;
  status: string;
  priority: string;
  dueDate?: string | null;
  dueMileage?: number | null;
};

const FleetVehicleDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id, fleetVehicleId } = useParams<{ id: string; fleetVehicleId: string }>();
  const [loading, setLoading] = useState(true);
  const [unit, setUnit] = useState<Unit | null>(null);
  const [timeline, setTimeline] = useState<TimelineItem[]>([]);
  const [alerts, setAlerts] = useState<Alert[]>([]);

  const loadData = useCallback(async () => {
    if (!id || !fleetVehicleId) return;
    setLoading(true);
    try {
      const response = await axios.get(`/api/fleets/${id}/vehicles/${fleetVehicleId}`);
      setUnit(response.data?.vehicle || null);
      setTimeline(response.data?.timeline || []);
      setAlerts(response.data?.alerts || []);
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'No se pudo cargar la unidad');
    } finally {
      setLoading(false);
    }
  }, [id, fleetVehicleId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) return <div className="p-6">Cargando...</div>;
  if (!unit) return <div className="p-6">Unidad no encontrada</div>;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{[unit.licensePlate, unit.make, unit.model].filter(Boolean).join(' - ') || unit.vehicleId}</h1>
          <p className="text-sm text-muted-foreground">Detalle de unidad de flota</p>
        </div>
        <Button variant="outline" onClick={() => navigate(`/fleets/${id}`)}>Volver a flota</Button>
      </div>

      <Card>
        <CardHeader><CardTitle>Ficha de unidad</CardTitle></CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-4 text-sm">
          <div><span className="text-muted-foreground">Estado:</span> <Badge variant="outline">{unit.operationalStatus || 'operativa'}</Badge></div>
          <div><span className="text-muted-foreground">Prioridad:</span> {unit.priority || '-'}</div>
          <div><span className="text-muted-foreground">Km actual:</span> {unit.currentMileage || '-'}</div>
          <div><span className="text-muted-foreground">Próximo servicio km:</span> {unit.nextServiceMileage || '-'}</div>
          <div><span className="text-muted-foreground">Próximo servicio fecha:</span> {unit.nextServiceDate || '-'}</div>
          <div><span className="text-muted-foreground">VTV:</span> {unit.vtvDueDate || '-'}</div>
          <div><span className="text-muted-foreground">Seguro:</span> {unit.insuranceDueDate || '-'}</div>
          <div><span className="text-muted-foreground">Año:</span> {unit.year || '-'}</div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Alertas de la unidad</CardTitle></CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader><TableRow><TableHead>Título</TableHead><TableHead>Vence</TableHead><TableHead>Estado</TableHead></TableRow></TableHeader>
              <TableBody>
                {alerts.length === 0 && <TableRow><TableCell colSpan={3}>Sin alertas</TableCell></TableRow>}
                {alerts.map((a) => (
                  <TableRow key={a.id}>
                    <TableCell>{a.title}</TableCell>
                    <TableCell>{a.dueDate || (a.dueMileage ? `${a.dueMileage} km` : '-')}</TableCell>
                    <TableCell><Badge variant={a.status === 'vencida' ? 'destructive' : 'outline'}>{a.status}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Historial</CardTitle></CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader><TableRow><TableHead>Fecha</TableHead><TableHead>Tipo</TableHead><TableHead>Detalle</TableHead><TableHead>Estado</TableHead><TableHead>Monto</TableHead></TableRow></TableHeader>
              <TableBody>
                {timeline.length === 0 && <TableRow><TableCell colSpan={5}>Sin movimientos</TableCell></TableRow>}
                {timeline.map((t, idx) => (
                  <TableRow key={`${t.source}-${t.sourceId}-${idx}`}>
                    <TableCell>{t.date ? String(t.date).slice(0, 10) : '-'}</TableCell>
                    <TableCell><Badge variant="secondary">{t.source}</Badge></TableCell>
                    <TableCell>{t.description || '-'}</TableCell>
                    <TableCell>{t.status || '-'}</TableCell>
                    <TableCell>{t.amount != null ? Number(t.amount).toLocaleString('es-AR') : '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FleetVehicleDetail;
